import { Injectable } from '@angular/core';
import { FormGroup } from '@angular/forms';
import { DialogBaseViewComponent, DialogEvents, DialogManager, DrawerManager, FormBase, FormSaveState } from '@sb/library/base';
import { Observable, filter, of, switchMap, take, tap } from 'rxjs';
import { UnsavedFormChangesDialogComponent } from './dialogs/unsaved-form-changes-dialog.component';

@Injectable({
    providedIn: 'root'
})
export class FormManager {
    // TODO: Remove this once we get rid of the old-style navigation in the TP. Needed to stop the animation from starting until the dialog has closed
    private _waitForDeactivationTP = false;

    constructor(
        private _DialogManager: DialogManager,
        private _DrawerManager: DrawerManager
    ) {
        this._DrawerManager.attemptingToCloseSubject.subscribe(() => {
            this.canDeactivate().subscribe(result => {
                this._DrawerManager.FinishDrawerCloseAttempt(result);
            })
        });
    }

    canDeactivate(currentForm?: FormGroup<any> | null, preserveCurrentForm: boolean = false): Observable<boolean> {
        if (currentForm === undefined) {
            currentForm = FormBase.currentForm;
        }

        // disabled and unsaved is meant to capture the transition state when moving between tabs in a multi-part form
        if (currentForm && (currentForm.isDirty || (currentForm.disabled && currentForm.saveState === FormSaveState.Unsaved))) {
            if (currentForm.autoSave) {
                setTimeout(currentForm.saveMethod!);
                return currentForm.saveSubject!.pipe(take(1), tap(saveSuccessful => {
                    if (saveSuccessful && !preserveCurrentForm) {
                        FormBase.currentForm = null;
                    }
                }));
            } else {
                this.WaitForDeactivationTP();
                const saveMethod = currentForm.saveMethod;

                let dialog: DialogBaseViewComponent<any>;
                if (currentForm.unsavedChangesDialog) {
                    dialog = this._DialogManager.CreateDialog(currentForm.unsavedChangesDialog, undefined, null);
                } else {
                    dialog = this._DialogManager.CreateDialog(UnsavedFormChangesDialogComponent, !!saveMethod, null);
                }

                return dialog.events.pipe(filter(event => [DialogEvents.PrimaryAction, DialogEvents.SecondaryAction, DialogEvents.Dismissed].includes(event)), take(1), switchMap(event => {
                    this.DeactivationCheckCompleteTP();
                    if (event === DialogEvents.PrimaryAction && saveMethod) {
                        setTimeout(saveMethod);
                        return currentForm!.saveSubject!.pipe(take(1), tap(saveSuccessful => {
                            if (saveSuccessful && !preserveCurrentForm) {
                                FormBase.currentForm = null;
                            }
                            dialog.DismissAction();
                        }));
                    } else if (event === DialogEvents.SecondaryAction || (event === DialogEvents.PrimaryAction && !saveMethod)) {
                        currentForm!.Reset();
                        if (!preserveCurrentForm) {
                            FormBase.currentForm = null;
                        }

                        dialog.DismissAction();
                        return of(true);
                    } else {
                        return of(false);
                    }
                }));
            }
        } else if (currentForm?.saveState === FormSaveState.Saving) {
            return currentForm!.saveSubject!.pipe(take(1), tap(saveSuccessful => {
                if (saveSuccessful && !preserveCurrentForm) {
                    FormBase.currentForm = null;
                }
            }));
        } else {
            return of(true);
        }
    }

    WaitForDeactivationTP(): void {
        this._waitForDeactivationTP = true;
    }

    DeactivationCheckCompleteTP(): void {
        this._waitForDeactivationTP = false;
    }

    get waitForDeactivationTP() {
        return this._waitForDeactivationTP;
    }
}
